import { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus,
  Filter,
  AlertCircle,
  Eye,
  RefreshCw,
  SlidersHorizontal,
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import InventoryMovementTotalsBar from '../components/transactions/InventoryMovementTotalsBar';
import { getInventoryAdjustments } from '../utils/inventoryAdjustmentApi';
import { ApiValidationError } from '../types/auth';
import type { InventoryAdjustment } from '../types/inventoryAdjustment';
import type { PaginationMeta } from '../types/journalEntry';
import { formatCurrency } from '../utils/formatCurrency';
import {
  adjustmentTotalValue,
  computeInventoryAdjustmentTotals,
  inventoryAdjustmentRowKey,
} from '../utils/inventoryAdjustmentSelection';
import {
  canCreateAdjustment,
  daysAgoIso,
  formatAdjustmentDate,
  formatAdjustmentLabel,
  formatAdjustmentTime,
  getAdjustmentStatusColor,
  todayIso,
} from '../utils/inventoryAdjustmentForm';

type StatusFilter = 'all' | 'open' | 'voided';

const STATUS_TABS: { id: StatusFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'open', label: 'Open' },
  { id: 'voided', label: 'Voided' },
];

const InventoryAdjustments = () => {
  const navigate = useNavigate();

  const [adjustments, setAdjustments] = useState<InventoryAdjustment[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [startDate, setStartDate] = useState(() => daysAgoIso(365));
  const [endDate, setEndDate] = useState(todayIso);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [page, setPage] = useState(1);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set());
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);

  const hasActiveFilters =
    startDate !== daysAgoIso(365) || endDate !== todayIso() || statusFilter !== 'all';

  const fetchAdjustments = async (pageNum: number) => {
    try {
      setLoading(true);
      setError(null);
      const result = await getInventoryAdjustments({
        start_date: startDate,
        end_date: endDate,
        ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
        per_page: 25,
        page: pageNum,
      });
      setAdjustments(result.adjustments);
      setMeta(result.meta);
      setPage(result.meta.current_page);
    } catch (err) {
      if (err instanceof ApiValidationError) {
        const firstError = Object.values(err.errors).flat()[0];
        setError(firstError || err.message);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to fetch inventory adjustments');
      }
      setAdjustments([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdjustments(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, statusFilter]);

  const clearFilters = () => {
    setStartDate(daysAgoIso(365));
    setEndDate(todayIso());
    setStatusFilter('all');
    setPage(1);
  };

  const openDetail = (id: number) => {
    navigate(`/inventory-adjustments/${id}`);
  };

  const pageTotals = useMemo(
    () => computeInventoryAdjustmentTotals(adjustments),
    [adjustments]
  );

  const visibleRowKeys = useMemo(
    () => adjustments.map(inventoryAdjustmentRowKey),
    [adjustments]
  );

  const selectedRows = useMemo(
    () => adjustments.filter((adj) => selectedKeys.has(inventoryAdjustmentRowKey(adj))),
    [adjustments, selectedKeys]
  );

  const selectedTotals = useMemo(
    () => computeInventoryAdjustmentTotals(selectedRows),
    [selectedRows]
  );

  const allPageSelected =
    visibleRowKeys.length > 0 && visibleRowKeys.every((key) => selectedKeys.has(key));

  const somePageSelected = visibleRowKeys.some((key) => selectedKeys.has(key));

  const toggleRow = (key: string, checked: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  };

  const toggleSelectAll = (checked: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) {
        for (const key of visibleRowKeys) {
          next.add(key);
        }
      } else {
        for (const key of visibleRowKeys) {
          next.delete(key);
        }
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedKeys(new Set());

  useEffect(() => {
    setSelectedKeys(new Set());
  }, [startDate, endDate, statusFilter, page]);

  useEffect(() => {
    const visibleKeySet = new Set(visibleRowKeys);
    setSelectedKeys((prev) => {
      const pruned = new Set([...prev].filter((key) => visibleKeySet.has(key)));
      return pruned.size === prev.size ? prev : pruned;
    });
  }, [visibleRowKeys]);

  useEffect(() => {
    const input = selectAllCheckboxRef.current;
    if (input) {
      input.indeterminate = somePageSelected && !allPageSelected;
    }
  }, [somePageSelected, allPageSelected]);

  const totalsBarMode = selectedTotals.count > 0 ? 'selected' : 'page';
  const totalsBarData = totalsBarMode === 'selected' ? selectedTotals : pageTotals;
  const totalsBarPageSuffix =
    meta && meta.total > pageTotals.count ? ' on this page' : '';
  const showTotalsBar = adjustments.length > 0;

  return (
    <div className="h-screen bg-gray-100 flex w-full overflow-hidden">
      <Sidebar />
      <main className="flex-1 flex flex-col min-h-0 overflow-hidden p-4 md:p-6 min-w-0">
        <div className="flex flex-1 flex-col min-h-0">
          <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="w-full max-w-full">
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-gray-900">
                  Inventory Adjustments
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  Correct stock levels for shrinkage, found stock, or count discrepancies
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => fetchAdjustments(page)}
                  disabled={loading}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg disabled:opacity-60"
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
                {canCreateAdjustment() && (
                  <Link
                    to="/manage-inventory-adjustments"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 text-sm font-medium"
                  >
                    <Plus className="h-5 w-5" />
                    New Adjustment
                  </Link>
                )}
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex gap-2 text-sm">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span className="flex-1">{error}</span>
              <button onClick={() => fetchAdjustments(page)} className="underline shrink-0">
                Retry
              </button>
            </div>
          )}

          <div className="mb-6 bg-white p-4 rounded-lg shadow">
            <div className="flex flex-wrap gap-2 mb-4">
              {STATUS_TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => {
                    setStatusFilter(tab.id);
                    setPage(1);
                  }}
                  className={`px-3 py-1.5 text-sm rounded-lg border ${
                    statusFilter === tab.id
                      ? 'bg-slate-800 text-white border-slate-800'
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setPage(1);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setPage(1);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="flex items-end">
                {hasActiveFilters && (
                  <div className="flex flex-wrap items-center gap-2 pb-2">
                    <Filter className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-500">
                      {meta?.total ?? adjustments.length} results
                    </span>
                    <button onClick={clearFilters} className="text-sm text-slate-700 underline">
                      Clear
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow overflow-x-auto">
            {loading ? (
              <div className="p-12 flex justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-800" />
              </div>
            ) : (
              <table className="w-full min-w-[900px] divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 w-10">
                      <input
                        ref={selectAllCheckboxRef}
                        type="checkbox"
                        checked={allPageSelected}
                        onChange={(e) => toggleSelectAll(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-slate-800"
                        aria-label="Select all on this page"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Number
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Account
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Memo
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Lines
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Value
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {adjustments.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-6 py-12 text-center">
                        <SlidersHorizontal className="mx-auto h-10 w-10 text-gray-400 mb-2" />
                        <p className="text-sm text-gray-500">No inventory adjustments found.</p>
                      </td>
                    </tr>
                  ) : (
                    adjustments.map((adj) => {
                      const totalValue = adjustmentTotalValue(adj);
                      const rowKey = inventoryAdjustmentRowKey(adj);
                      const isCheckboxSelected = selectedKeys.has(rowKey);
                      return (
                        <tr
                          key={rowKey}
                          className={`hover:bg-gray-50 cursor-pointer${
                            isCheckboxSelected ? ' bg-slate-50/60' : ''
                          }`}
                          onClick={() => openDetail(adj.id)}
                        >
                          <td className="px-4 py-3 w-10" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={isCheckboxSelected}
                              onChange={(e) => toggleRow(rowKey, e.target.checked)}
                              className="h-4 w-4 rounded border-gray-300 text-slate-800"
                              aria-label={`Select ${adj.adjustment_number}`}
                            />
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 font-mono">
                            {adj.adjustment_number}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <div>{formatAdjustmentDate(adj.adjustment_date)}</div>
                            <div className="text-xs text-gray-400 mt-0.5">
                              {formatAdjustmentTime(adj.created_at)}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span
                              className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${getAdjustmentStatusColor(adj.status ?? 'OPEN')}`}
                            >
                              {formatAdjustmentLabel(adj.status ?? 'OPEN')}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {adj.adjustment_account.name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 max-w-[200px] truncate">
                            {adj.memo || '—'}
                          </td>
                          <td className="px-4 py-3 text-sm text-right text-gray-700">
                            {adj.lines.length}
                          </td>
                          <td
                            className={`px-4 py-3 text-sm text-right font-medium font-mono ${
                              totalValue >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}
                          >
                            {formatCurrency(totalValue)}
                          </td>
                          <td
                            className="px-4 py-3 text-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              title="View"
                              onClick={() => openDetail(adj.id)}
                              className="p-1.5 rounded text-slate-600 hover:bg-slate-50"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </div>

          {meta && meta.last_page > 1 && (
            <div className="flex items-center justify-between mt-4 px-1">
              <p className="text-sm text-gray-500">
                Page {meta.current_page} of {meta.last_page} · {meta.total} total
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => fetchAdjustments(meta.current_page - 1)}
                  disabled={meta.current_page <= 1 || loading}
                  className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-white disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => fetchAdjustments(meta.current_page + 1)}
                  disabled={meta.current_page >= meta.last_page || loading}
                  className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-white disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
          </div>

          {showTotalsBar && !loading && (
            <InventoryMovementTotalsBar
              mode={totalsBarMode}
              count={totalsBarData.count}
              qtyChange={0}
              valueChange={totalsBarData.valueChange}
              hideQty
              pageCountSuffix={totalsBarPageSuffix}
              onClear={totalsBarMode === 'selected' ? clearSelection : undefined}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default InventoryAdjustments;
